import { createContext, useContext } from 'react';
import { cn } from './cn';
import { focusRing } from './focus';

/** What a `Field` hands to the control inside it: its id, the ids of its error and hint, and whether it is invalid. */
export type FieldState = { id: string; describedBy: string | undefined; invalid: boolean };
export const FieldContext = createContext<FieldState | null>(null);

/**
 * The look shared by Input, Textarea and Select. Under a coarse pointer the text is at least 16 px: iOS Safari zooms
 * the page on focus into any field below that, and the viewport stays zoomable (index.html pins `maximum-scale=1`
 * only where the editor needs it).
 */
export const controlClass = cn(
  'w-full rounded-xl border border-border-strong bg-surface px-3 font-sans text-sm text-fg placeholder:text-fg-subtle-text transition-colors duration-(--duration-fast) hover:border-fg-muted disabled:opacity-50 aria-invalid:border-danger pointer-coarse:[font-size:max(16px,1em)]',
  focusRing,
);

/** The id and the ARIA links of the surrounding `Field`, spread on the control; nothing outside a `Field`. */
export function useField(): { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: true } {
  const f = useContext(FieldContext);
  return f ? { id: f.id, 'aria-describedby': f.describedBy, 'aria-invalid': f.invalid || undefined } : {};
}
